import {
  loadSVGFromURL,
  util,
  Group,
  type FabricObject,
  type Canvas,
} from 'fabric';
import { isDugout } from '@/config/features';
import { ALTERNATE_ICON_COLOR } from '@/config/dugout';

export interface AddItemProps {
  name: string;
  src: string;
  left: number;
  top: number;
  variation?: string;
  canvas: Canvas;
  width: number | string | undefined;
  height: number | string | undefined;
  defaultIcon?: unknown;
  alternateIcon?: unknown;
  scaleFactor: number;
  // The resolved theme (config/theme.ts). Passed in because these helpers run outside React — reading
  // process.env here would pin them to the BUILD theme.
  theme: string;
  // extra fields some callers pass (color/iconType/fontColor) are accepted + ignored here
  color?: string;
  iconType?: string;
  fontColor?: string;
}

const itemCount = 1;

const addItem = (props: AddItemProps): void => {
  const {
    name,
    src,
    left,
    top,
    variation,
    canvas,
    width,
    height,
    defaultIcon,
    alternateIcon,
    scaleFactor,
    iconType,
    color,
  } = props;
  if (!variation) {
    console.error('No item variation provided to addItem.js');
    return;
  }

  // loadSVGFromURL is Promise-based; the resolved value is { objects, options, … }.
  void loadSVGFromURL(src).then(({ objects, options }) => {
    const parsed = objects.filter(
      (object): object is FabricObject => object !== null,
    );

    // Dugout recolours its shared item SVGs at creation (gated off for the pre-coloured themes):
    // symbol mode paints the main shape neutral grey; a cap's disc (`js-main-color`) takes its `color`.
    if (isDugout(props.theme)) {
      if (iconType === 'alternateIcon') {
        parsed[0]?.set({ fill: ALTERNATE_ICON_COLOR });
      } else if (variation.includes('cone') && color) {
        // `js-main-color` is the recolourable disc; it is also the first element of `cone.svg`, so
        // fall back to index 0 in case the SVG parser did not carry the `id` through.
        const mainShape =
          parsed.find((o) => o.id === 'js-main-color') ?? parsed[0];
        mainShape?.set({ fill: color });
      }
    }

    const svgData = util.groupSVGElements(parsed, options);

    svgData.name = name;

    svgData.set({
      originLeft: (left / scaleFactor) * 1,
      originTop: (top / scaleFactor) * 1,
      left,
      top,
      originX: 'center',
      originY: 'center',
      scaleX: scaleFactor,
      scaleY: scaleFactor,
      sourcePath: src, // this tells the import/export to use this path, instead of embedding the code in the JSON
    });

    svgData.scaleToWidth(Number(width));
    svgData.scaleToHeight(Number(height));

    // add svgData into a group, so it's consistent with the way we create shirts
    const group = new Group([svgData], {
      id: `item-${itemCount}`,
      name,
      originLeft: (left / scaleFactor) * 1,
      originTop: (top / scaleFactor) * 1,
      scaleX: scaleFactor,
      scaleY: scaleFactor,
      left,
      top,
      originX: 'center',
      originY: 'center',
      // images dont need to be stretched, rotated, etc. keep it simple
      hasBorders: true,
      variation,
      defaultIcon,
      alternateIcon,
      // Dugout re-derives an item's colour on shape-toggle/import, so it stores `color` on the group;
      // the other themes never read it back (pre-coloured SVGs), so it stays off their objects.
      ...(isDugout(props.theme) && { color }),
    });

    canvas.setActiveObject(group);
    canvas.add(group);
  });
};

export default addItem;
